import React from 'react';
import { useLocation } from 'react-router-dom';

import { useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { List } from '@common/typescript/objects/List';

import { rest } from '@app/components/Api';
import { transformStateList, transformVetList } from '@app/components/LocalCommon/Utils/transformList';
import LoadMoreButton from '@app/components/UI/LoadMoreButton/LoadMoreButton';
import Sidebar from '@app/components/UI/Sidebar/Sidebar';
import VetTesCard from '@app/components/UI/VetTesCard/VetTesCard';
import { useIsMobile } from '@app/hooks/useIsMobile';
import { ListOption } from '@app/objects/SidebarProps';
import { StatesListItem } from '@app/objects/StatesListItem';
import { VetTestimonialsRes } from '@app/objects/VetTestimonialsRes';
import { Veterinarian } from '@app/objects/Veterinarian';

interface VetTestimonialsBodyProps {
	stateSelected: string;
	setStateSelected: (e: string) => void;
	cardsCount: number;
	setCardsCount: (e: number) => void;
	setModalVisible: (e: boolean) => void;
}

const VetTestimonialsBody: React.FC<VetTestimonialsBodyProps> = ({
	stateSelected,
	setStateSelected,
	cardsCount,
	setCardsCount,
	setModalVisible,
}) => {
	const location: any = useLocation();
	const [currentIndex, setCurrentIndex] = React.useState<number>(0);
	const navCollapse = useIsMobile();
	const [stateList, setStateList] = React.useState<Array<StatesListItem>>([]);
	const [vetIdList, setVetIdList] = React.useState<Array<Veterinarian>>([]);
	const [vetId, setVetId] = React.useState<string>('');
	const [firstBootVeterinarians, setFirstBootVeterinarians] = React.useState<boolean>(false);
	const [vetNameList, setVetNameList] = React.useState<Array<Veterinarian>>([]);
	const [vetName, setVetName] = React.useState<string>('');
	const [vetNameShort, setVetNameShort] = React.useState<string>('');
	const [vetFieldValue, setVetFieldValue] = React.useState<string>('');
	const [firstRender, setFirstRender] = React.useState<boolean>(false);
	const stateListOptions: Array<ListOption> = [];
	const vetListOptions: Array<ListOption> = [];
	const vetNameListOptions: Array<ListOption> = [];

	const context = useItemsProviderContext<VetTestimonialsRes>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { loading, items },
		actions: { handleChange },
	} = context;

	const onSetIssue = (index) => {
		setCurrentIndex(index);
	};

	const onSubmit = () => {
		setModalVisible(true);
	};

	const getVeterinarians = (name: string, stateSelected: string = '', count: number = 300): Promise<Array<Veterinarian>> => {
		return new Promise<Array<Veterinarian>>((resolve) => {
			rest.get<List<Veterinarian>>(`v1/veterinarians${stateSelected.length ? `/${stateSelected}` : ''}?count=${count}&name=${name}`)
				.then((res) => resolve(res.list));
		});
	};

	const getStates = () => {
		rest.get<List<StatesListItem>>('v1/states')
			.then((res) => setStateList(res.list));
	};

	React.useEffect(() => {
		if (!location?.state?.navCollapse) {
			if (location?.state?.vetId) {
				setVetId(location.state.vetId);
				setVetFieldValue(location.state.name);
			}
		}

		if (location?.state?.navCollapse) {
			if (location?.state?.name) {
				setVetName(location.state.name);
			}
		}
	}, []);

	React.useEffect(() => {
		getVeterinarians('')
			.then((veterinarians) => {
				setVetNameList(veterinarians);
				setVetIdList(veterinarians);
				setFirstBootVeterinarians(true);
			});

		getStates();

		setFirstRender(true);
	}, []);

	React.useEffect(() => {
		if (firstRender) {
			handleChange({
				offset: 0,
				count: cardsCount,
				veterinarianId: vetId,
				veterinarianName: vetName.substring(0, 4) === 'Dr. ' ? vetName.slice(4) : vetName,
			});
		}
	}, [vetId, vetName, stateSelected]);

	React.useEffect(() => {
		if (firstBootVeterinarians) {
			const debounceTimeout = setTimeout(() => {
				getVeterinarians(vetNameShort)
					.then((veterinarians) => {
						setVetNameList(veterinarians);
					});
			}, 300);

			return () => clearTimeout(debounceTimeout);
		}
	}, [vetNameShort]);

	React.useEffect(() => {
		if (firstBootVeterinarians) {
			const debounceTimeout = setTimeout(() => {
				getVeterinarians(vetFieldValue, stateSelected)
					.then((veterinarians) => {
						setVetIdList(veterinarians);
					});
			}, 300);

			return () => clearTimeout(debounceTimeout);
		}
	}, [vetFieldValue]);

	transformStateList(stateList, stateListOptions);
	transformVetList(vetIdList, vetListOptions);
	transformVetList(vetNameList, vetNameListOptions);

	return (
		<div className="vet-testimonials__body">
			<Sidebar
				searchFieldName="vetByName"
				searchFieldPlaceHolder="Search testimonials by vet name"
				onSetIssue={onSetIssue}
				currentIndex={currentIndex}
				navCollapse={false}
				topics={null}
				withSubmit
				submitCaption="Submit a testimonial"
				typeButton="button"
				setStateSelected={setStateSelected}
				stateSelected={stateSelected}
				vetId={vetId}
				setVetId={setVetId}
				setVetName={setVetName}
				setVetNameShort={setVetNameShort}
				vetNameShort={vetNameShort}
				vetFieldValue={vetFieldValue}
				setVetFieldValue={setVetFieldValue}
				stateListOptions={stateListOptions}
				vetListOptions={vetListOptions}
				vetNameListOptions={vetNameListOptions}
				setCardsCount={setCardsCount}
				onSubmit={onSubmit}
				initCardsCount={4}
				vetName={vetName}
			/>

			<div className="vet-testimonials__content">
				{!loading ? (
					items.length ? (
						items.map((item) => (
							<VetTesCard
								key={item.id}
								tes={item}
								navCollapse={navCollapse}
							/>
						))
					) : (
						<div className="format-error-message">
							Woof! We were not able to find a match. Please try another search.
						</div>
					)
				) : <BounceLoader />}

				<LoadMoreButton
					visible={!loading}
					textButton="Load more testimonials"
					className="btn btn-secondary btn-load-more"
				/>
			</div>
		</div>
	);
};

export default VetTestimonialsBody;
